import type { SharedSnapshot } from './protocol';
import { t } from '../i18n/I18n';

export type SharedRecent = { documentId: string; name: string; openedAt: number };
const uuid = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;
const tokenPattern = /^[A-Za-z0-9_-]{43}$/;
const indexKey = 'hp.shared.recent';

function read(key: string): unknown {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
export function sharedDocumentId(projectId: string): string | null {
  const direct = projectId.startsWith('shared-') ? projectId.slice(7) : null;
  if (direct && uuid.test(direct)) return direct;
  const id = read('hp.shared.' + projectId);
  return typeof id === 'string' && uuid.test(id) ? id : null;
}
export function sharedRecents(): SharedRecent[] {
  const value = read(indexKey);
  return Array.isArray(value) ? value.filter((entry): entry is SharedRecent => !!entry && uuid.test(entry.documentId)
    && typeof entry.name === 'string' && Number.isFinite(entry.openedAt)).slice(0, 40) : [];
}
export function rememberShared(snapshot: SharedSnapshot, sourceId?: string, inviteToken?: string): void {
  const recent = { documentId: snapshot.documentId, name: snapshot.name, openedAt: Date.now() };
  localStorage.setItem(indexKey, JSON.stringify([recent, ...sharedRecents().filter(value => value.documentId !== snapshot.documentId)].slice(0, 40)));
  if (sourceId && !sourceId.startsWith('shared-')) localStorage.setItem('hp.shared.' + sourceId, JSON.stringify(snapshot.documentId));
  if (inviteToken && snapshot.self.role === 'owner') saveInviteToken(snapshot.documentId, inviteToken);
  if (sourceId) localStorage.removeItem('hp.shared.create.' + sourceId);
}
export function savedDisplayName(): string {
  try { return localStorage.getItem('hp.collab.displayName') || ''; } catch { return ''; }
}
export function saveDisplayName(name: string): void { localStorage.setItem('hp.collab.displayName', name.trim()); }
export function saveInviteToken(documentId: string, token: string | null): void {
  localStorage.setItem('hp.collab.invite.' + documentId, JSON.stringify(token));
}
export function parseSharedLink(raw: string): { documentId: string; invite: string | null } | null {
  try {
    const url = new URL(raw), documentId = url.searchParams.get('shared');
    if (!['https:', 'http:'].includes(url.protocol) || !documentId || !uuid.test(documentId)) return null;
    const invite = new URLSearchParams(url.hash.slice(1)).get('invite');
    return invite && !tokenPattern.test(invite) ? null : { documentId, invite };
  } catch { return null; }
}
export function collaborationError(error: unknown): string {
  const code = error && typeof error === 'object' && 'code' in error ? error.code : null;
  if (code === 'forbidden') return t('#CollabErrorForbidden');
  if (code === 'invalid') return t('#CollabErrorInvalidLink');
  if (code === 'duplicate_mismatch') return t('#CollabErrorRepeat');
  if (error instanceof DOMException && error.name === 'QuotaExceededError') return t('#CollabErrorStorageFull');
  if (error instanceof DOMException && error.name === 'SecurityError') return t('#CollabErrorStorageBlocked');
  return t('#CollabErrorConnect');
}
